import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { previewSegmentationRule } from '../../services/api';

const DEBOUNCE_MS = 400;

export default function RulePreviewPanel({ envelope, sampleSize = 10 }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!envelope) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await previewSegmentationRule({
          ruleJson: JSON.stringify(envelope),
          sampleSize,
        });
        setResult(res.data);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Preview failed.');
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [envelope, sampleSize]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Preview</Typography>
        {loading && <CircularProgress size={14} />}
        {!loading && result && (
          <>
            <Chip
              size="small"
              color={result.isValid ? 'primary' : 'default'}
              label={`${result.totalMatches ?? 0} matches`}
            />
            <Chip size="small" variant="outlined" label={`${result.durationMs ?? 0} ms`} />
          </>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {result?.errors?.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {result.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </Alert>
      )}
      {result?.warnings?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </Alert>
      )}

      {result?.sample?.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Industry</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.sample.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.companyName}</TableCell>
                  <TableCell>{row.industryName || '—'}</TableCell>
                  <TableCell>{row.country || '—'}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell align="right">{row.azotteFitScore ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        result?.isValid && !loading && (
          <Typography variant="body2" color="text.secondary">
            No matching leads.
          </Typography>
        )
      )}
    </Paper>
  );
}
